import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  isWindowedChannel,
  latestTimestamp,
  normalizeChannel,
  replyWindowFor,
  type ReplyWindow,
} from "@/lib/replyWindow";

interface ConversationRef {
  channel?: string | null;
  channel_user_id?: string | null;
  customer_phone?: string | null;
}

/** The 24h reply window for a conversation's customer. Looks across every
 *  conversation with the same business, channel and recipient (the same rule
 *  send-channel-message applies). `seenCustomerAt` is the newest customer
 *  message already on screen, so a message arriving over realtime reopens the
 *  window at once. Re-renders when the window closes. */
export function useReplyWindow(
  businessId: string | null | undefined,
  conversation: ConversationRef | null | undefined,
  seenCustomerAt: string | null,
): ReplyWindow & { refresh: () => void } {
  const channel = normalizeChannel(conversation?.channel);
  const recipient = conversation?.channel_user_id || conversation?.customer_phone || null;
  const enabled = !!businessId && !!recipient && isWindowedChannel(channel);

  const query = useQuery({
    queryKey: ["reply-window", businessId, channel, recipient],
    enabled,
    queryFn: async (): Promise<string | null> => {
      const { data: convs, error } = await supabase
        .from("ai_conversations")
        .select("id")
        .eq("business_id", businessId!)
        .eq("channel", channel)
        .eq("channel_user_id", recipient!);
      if (error) throw error;
      const ids = (convs ?? []).map((c) => c.id);
      if (ids.length === 0) return null;
      const { data: last, error: msgError } = await supabase
        .from("ai_messages")
        .select("created_at")
        .in("conversation_id", ids)
        .eq("sender_type", "customer")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (msgError) throw msgError;
      return last?.created_at ?? null;
    },
  });

  // Unknown until the lookup succeeds, unless a message on screen already
  // proves the window is open.
  const lastCustomerAt = query.isSuccess
    ? latestTimestamp(query.data, seenCustomerAt)
    : seenCustomerAt ?? undefined;
  let state = replyWindowFor(channel, lastCustomerAt, Date.now());
  if (!query.isSuccess && state.open === false) state = { ...state, open: null };

  const [, setTick] = useState(0);
  const closesAtMs = state.open && state.closesAt ? state.closesAt.getTime() : null;
  useEffect(() => {
    if (closesAtMs == null) return;
    const t = setTimeout(() => setTick((n) => n + 1), Math.max(0, closesAtMs - Date.now()) + 1000);
    return () => clearTimeout(t);
  }, [closesAtMs]);

  return { ...state, refresh: () => void query.refetch() };
}
